// src/components/BillsPassed.jsx
// "Bills Passed" tab (city tenants): every passed vote, grouped by the same
// quality-of-life categories as the Analysis tab (renamed from "Prediction"),
// and within each category by the same purpose buckets as the "What passed
// bills actually did" donut.
// Deep-link targets:
//   #bp-<categoryId>            — a category section (from Analysis chart rows)
//   #bp-<categoryId>-<impactId> — a purpose subsection (from donut slices/legend)
//   ?cat=<categoryId>&district=<n> — narrow to one supervisorial district's
//                                    bills within a category (from the map)
// Each bill links onward to the Voting tab's existing #vote-<id> deep link.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { fetchAllRows } from '../utils/fetchAllRows';
import SubscribeGate from '../auth/SubscribeGate';
import { CATEGORIES, OTHER, classify, votePassed } from './billCategories';
import { IMPACT_TYPES, classifyImpact, districtsForIssue, DISTRICTS } from './BillImpact';
import './BillsPassed.css';

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear() - 2000}`;
};

function BillsPassedContent({ districtId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [votings, setVotings] = useState([]);

  /* Optional narrowing from the map: ?cat=<id>&district=<n>. Both are needed —
     the map's counts are per-category, so a district alone would show a
     different (larger) set than the number that was clicked. */
  const { filterCatId, filterDistrict } = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const num = Number(p.get('district'));
    return {
      filterCatId: p.get('cat'),
      filterDistrict: Number.isInteger(num) && num > 0 ? num : null,
    };
  }, [location.search]);

  const filterDistrictMeta = filterDistrict
    ? DISTRICTS.find((d) => d.id === filterDistrict)
    : null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: err } = await fetchAllRows(() =>
        supabase
          .from('Votings')
          .select('id, voteDate, category, issue, outcome')
          .eq('districtId', districtId)
          .order('voteDate', { ascending: false })
          .order('id', { ascending: false })
      );
      if (cancelled) return;
      if (err) setError(err);
      else setVotings(data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  /* Passed bills → category → impact type. Same classifiers as Analysis and
     the donut, so a count clicked there matches the list length here. */
  const grouped = useMemo(() => {
    const allCats = [...CATEGORIES, OTHER];
    const byCat = new Map();
    let total = 0;
    let unfilteredInCat = 0;
    for (const v of votings) {
      if (!votePassed(v.outcome)) continue;
      const catId = classify(`${v.category || ''} ${v.issue || ''}`, v.category);
      const impactId = classifyImpact(v.issue);

      if (filterDistrict) {
        // Mirror the map exactly: same category, substantive only (a
        // continuance names a place but moves nothing), and naming this
        // district. Anything else would show a count the map never claimed.
        if (filterCatId && catId !== filterCatId) continue;
        unfilteredInCat += 1;
        if (impactId === 'procedural') continue;
        if (!districtsForIssue(v.issue).includes(filterDistrict)) continue;
      }

      total += 1;
      let cat = byCat.get(catId);
      if (!cat) {
        cat = new Map();
        byCat.set(catId, cat);
      }
      if (!cat.has(impactId)) cat.set(impactId, []);
      cat.get(impactId).push(v);
    }
    const sections = allCats
      .map((cat) => {
        const impacts = byCat.get(cat.id);
        if (!impacts) return null;
        const count = Array.from(impacts.values()).reduce((s, a) => s + a.length, 0);
        // Substantive purposes first (by size), procedural pinned last.
        const subs = IMPACT_TYPES
          .filter((t) => impacts.has(t.id))
          .map((t) => ({ type: t, bills: impacts.get(t.id) }))
          .sort((a, b) => {
            if (a.type.id === 'procedural') return 1;
            if (b.type.id === 'procedural') return -1;
            return b.bills.length - a.bills.length;
          });
        return { cat, count, subs };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.cat.id === 'other') return 1;
        if (b.cat.id === 'other') return -1;
        return b.count - a.count;
      });
    return { sections, total, unfilteredInCat };
  }, [votings, filterDistrict, filterCatId]);

  /* Resolve a #bp-... deep link once the lists exist: scroll to the section or
     subsection and flash it. Re-runs when the hash changes, so a second click
     from the Analysis tab retargets correctly. */
  useEffect(() => {
    if (loading) return undefined;
    const hash = location.hash;
    if (!hash || !hash.startsWith('#bp-')) return undefined;
    const id = hash.substring(1);
    // Let the sections paint first.
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('bp-flash');
      setTimeout(() => el.classList.remove('bp-flash'), 4000);
    }, 150);
    return () => clearTimeout(timer);
  }, [loading, location.hash]);

  if (loading) return <div className="bp-card bp-status">Loading passed bills…</div>;
  if (error) return <div className="bp-card bp-status bp-status-error">Error: {error.message}</div>;

  const filterCat = filterCatId
    ? [...CATEGORIES, OTHER].find((c) => c.id === filterCatId)
    : null;
  const clearFilter = () =>
    navigate(`?tab=billspassed${filterCatId ? `#bp-${filterCatId}` : ''}`);

  /* District filter that matched nothing — reachable by hand-editing the URL,
     or if the classifiers change under a stale link. */
  if (filterDistrict && !grouped.sections.length) {
    return (
      <div className="bp-card">
        <div className="bp-filter">
          <div>
            <strong>District {filterDistrict}</strong>
            {filterDistrictMeta ? ` — ${filterDistrictMeta.name}` : ''}
            {filterCat ? ` · ${filterCat.label}` : ''}
          </div>
          <button type="button" className="bp-filter-clear" onClick={clearFilter}>
            Show all bills
          </button>
        </div>
        <p className="bp-status">
          No passed bills here name a place in this district.
        </p>
      </div>
    );
  }

  if (!grouped.sections.length) {
    return <div className="bp-card bp-status">No passed bills recorded yet for this district.</div>;
  }

  return (
    <>
      <div className="bp-card">
        <h2 className="bp-title">Bills Passed</h2>

        {filterDistrict ? (
          <>
            <div className="bp-filter">
              <div>
                Showing <strong>{grouped.total}</strong> bill
                {grouped.total === 1 ? '' : 's'} in{' '}
                <strong>
                  District {filterDistrict}
                  {filterDistrictMeta ? ` — ${filterDistrictMeta.name}` : ''}
                </strong>
                {filterCat ? (
                  <>
                    {' '}from <strong>{filterCat.label}</strong>
                  </>
                ) : null}
              </div>
              <button type="button" className="bp-filter-clear" onClick={clearFilter}>
                Show all bills
              </button>
            </div>
            <p className="bp-note bp-note-tight">
              These are the bills the map counted for this district: passed,
              substantive (procedural votes excluded), and naming a place in
              District {filterDistrict}
              {filterCat ? (
                <> — {grouped.total} of {grouped.unfilteredInCat} passed {filterCat.label} bills</>
              ) : null}
              . A bill naming several districts appears under each.
            </p>
          </>
        ) : (
          <p className="bp-sub">
            All {grouped.total} passed votes, grouped by the same categories and
            purposes as the Analysis tab. Click a bill&rsquo;s date to see the full
            roll call on the Voting tab.
          </p>
        )}

        <div className="bp-toc">
          {grouped.sections.map((s) => (
            <button
              key={s.cat.id}
              type="button"
              className="bp-toc-item"
              onClick={() => {
                const el = document.getElementById(`bp-${s.cat.id}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {s.cat.label} <span className="bp-toc-n">{s.count}</span>
            </button>
          ))}
        </div>
        <p className="bp-note">
          Categories and purposes are keyword-derived from each bill&rsquo;s text and
          are approximate — the same classification the Analysis charts use, so
          counts match between the two tabs. &ldquo;Passed&rdquo; means yes votes
          outnumbered no votes in the recorded tally.
        </p>
      </div>

      {grouped.sections.map((s) => (
        <div className="bp-card bp-section" id={`bp-${s.cat.id}`} key={s.cat.id}>
          <h3 className="bp-cat-title" title={s.cat.hint}>
            {s.cat.label}
            <span className="bp-cat-n">{s.count} passed</span>
          </h3>
          {s.subs.map(({ type, bills }) => (
            <div className="bp-sub-block" id={`bp-${s.cat.id}-${type.id}`} key={type.id}>
              <h4 className="bp-sub-title" title={type.hint}>
                {type.label}
                <span className="bp-sub-n">{bills.length}</span>
              </h4>
              <ul className="bp-list">
                {bills.map((b) => {
                  // Show where each bill lands. Under a district filter every
                  // row matches, so the badge is the "location" readout; in the
                  // full list it's extra context on the bills that have one.
                  const places = districtsForIssue(b.issue);
                  return (
                    <li
                      key={b.id}
                      className={`bp-bill${filterDistrict ? ' bp-bill-match' : ''}`}
                    >
                      <button
                        type="button"
                        className="bp-bill-date"
                        title="See the full roll call on the Voting tab"
                        onClick={() => navigate(`?tab=voting#vote-${b.id}`)}
                      >
                        {fmtDate(b.voteDate)}
                      </button>
                      <span className="bp-bill-issue">
                        {b.issue}
                        {places.length > 0 && (
                          <span className="bp-bill-districts">
                            {places.map((n) => {
                              const meta = DISTRICTS.find((x) => x.id === n);
                              return (
                                <span
                                  key={n}
                                  className={`bp-dbadge${n === filterDistrict ? ' bp-dbadge-on' : ''}`}
                                  title={meta ? `District ${n} — ${meta.name}` : `District ${n}`}
                                >
                                  D{n}
                                </span>
                              );
                            })}
                          </span>
                        )}
                      </span>
                      <span className="bp-bill-outcome" title="Yes–No tally">{b.outcome}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/* ── Tab entry point: district lookup, then the same gate as Analysis ── */
function BillsPassed() {
  const { abbreviation } = useParams();
  const [district, setDistrict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setDistrict(null);
      const { data, error: err } = await supabase
        .from('Districts')
        .select('id, premium_gate')
        .eq('abbreviation', abbreviation)
        .single();
      if (cancelled) return;
      if (err || !data) setError(err || { message: 'District not found' });
      else setDistrict(data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [abbreviation]);

  if (loading) {
    return (
      <div className="bp-tab">
        <div className="bp-card bp-status">Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bp-tab">
        <div className="bp-card bp-status bp-status-error">Error: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="bp-tab">
      <SubscribeGate premiumGate={district?.premium_gate} featureName="Bills Passed">
        <BillsPassedContent districtId={district.id} />
      </SubscribeGate>
    </div>
  );
}

export default BillsPassed;
